import styles from "./css/index.module.css";
import GadgetBuilderScreenshot from "../../images/GadgetBuilderScreenshot.png";
import GroupAdminScreenshot from "../../images/GroupAdminScreenshot.png";
import DesktopClientScreenshot from "../../images/DesktopClientScreenshot.png";
import { RightImageAllignedContainer } from "./components/RightImageAllignedContainer";
import { LeftImageAllignedContainer } from "./components/LeftImageAllignedContainer";
import GarudaInstallerSelectionButton from "../Home/components/GarudaInstallerSelectionButton";
import { Card, CardHeader, Divider, Grid, Hidden, Stack, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faChartLine,
  faClone,
  faCubes,
  faFingerprint,
  faGlobe,
  faPalette,
  faRocket,
  faWrench,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";

const aboutGadgetBuilderText =
  "Garuda is known for its intelligent eco system. Every system consists of a very fundamental basic element which is called the agent in Garuda system. A agent is the primary element of Garuda, which is capable of performing a specific task. Agent Builder is the tool we use to Garudify any executable program or any software.";
const aboutDesktopClientText =
  "Garuda Desktop is a desktop application which lets the user perform tasks related to agents and recipes. It acts as a platform to install and run agents.";
const aboutGroupAdminPortal =
  "Group Admin Portal provides a platform to generate licenses to run and/or create agents inside Garuda as well as giving the group admins option to create users for a specific role e.g. Developer, Normal Users for that specific group.";

interface AboutPortalContentProps {
  headerText: string;
  portalFirstName: string;
  portalSecondName: string;
  infoText: string;
  shouldContainDownloadButton?: boolean;
}

const AboutPortalContent = ({
  headerText,
  portalFirstName,
  portalSecondName,
  infoText,
  shouldContainDownloadButton = false,
}: AboutPortalContentProps) => {

  return (
    <>
      <Typography variant="h4" className={styles.builtForYouText}>{headerText}</Typography>
      <Typography variant="h1" className={styles.platfromText}>
        {portalFirstName} <span>{portalSecondName}</span>
      </Typography>
      <Typography variant="body2" className={styles.infoText}>{infoText}</Typography>
      {
        shouldContainDownloadButton && <Hidden mdDown><GarudaInstallerSelectionButton /></Hidden>}
      {/* {shouldContainDownloadButton ? (
        <GarudaInstallerSelectionButton />
      ) : (
        <ExploreMoreButton
          label={`${portalFirstName} 
                    ${portalSecondName}`}
          dataTestId={exploreButtonLabelTestId}
        />
      )} */}
    </>
  );
};

interface WhatsNewCardProps {
  title: string;
  subtitle: string;
  icon: IconDefinition;
}

const WhatsNewCard = ({ title, subtitle, icon }: WhatsNewCardProps) => {
  return (
    <Grid item xs={12} md={4}>
      <Card className={styles.whatsNewCard}>
        <CardHeader
          avatar={<FontAwesomeIcon icon={icon} size="2x" color="#44bcdb" />}
          title={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
          }
          subheader={
            <Typography variant="subtitle1" sx={{ color: "#454545" }}>
              {subtitle}
            </Typography>
          }
        />
      </Card>
    </Grid>
  );
};

export const WhatsNewInGaruda = () => {
  return (
    <div className={styles.whatsNewContainer}>
      <Typography variant="h4" sx={{margin: "1rem 0"}}>What's new in Garuda2x?</Typography>
      <Typography variant="body1">
        Explore the latest features and enhancements designed to improve your
        experience.
      </Typography>
      <Grid container spacing={2}>
        <WhatsNewCard
          title="Native Desktop Experience"
          subtitle="Rebuilt as a JavaFX desktop app for unmatched reliability."
          icon={faRocket}
        />
        <WhatsNewCard
          title="Cross-platform support"
          subtitle="Works seamlessly on both Windows and Mac."
          icon={faGlobe}
        />
        <WhatsNewCard
          title="Lightweight & Fast"
          subtitle="Optimized for speed and responsiveness."
          icon={faBolt}
        />
        <WhatsNewCard
          title="Modern Design"
          subtitle="Sleek interface for a better user experience."
          icon={faPalette}
        />
        <WhatsNewCard
          title="Multiple Instances"
          subtitle="Manage and run multiple agents simultaneously."
          icon={faClone}
        />
        <WhatsNewCard
          title="Smarter Device Identification"
          subtitle="Uses serial numbers or manufacturer details for accuracy."
          icon={faFingerprint}
        />
        <WhatsNewCard
          title="Bulk Agent Installation"
          subtitle="Upload and install multiple agents in one go."
          icon={faCubes}
        />
        <WhatsNewCard
          title="Real-Time Monitoring"
          subtitle="Track memory usage with an intuitive resource monitor."
          icon={faChartLine}
        />
        <WhatsNewCard
          title="Unified Installer"
          subtitle="One installer for both online and offline use, with JRE included."
          icon={faWrench}
        />
      </Grid>
    </div>
  );
};

interface WhyChooseGarudaItemProps {
  title: string;
  textContent: string;
}

export const WhyChooseGarudaItem = ({
  title,
  textContent,
}: WhyChooseGarudaItemProps) => {
  return (
    <Grid item xs={12} md={4}>
      <Card className={styles.whyChooseGarudaCard}>
        <CardHeader
          title={
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#44bcdb" }}
            >
              {title}
            </Typography>
          }
          subheader={
            <Typography variant="subtitle1" sx={{ color: "#454545" }}>
              {textContent}
            </Typography>
          }
        />
      </Card>
    </Grid>
  );
};

export const WhyChooseGaruda = () => {
  return (
    <div className={styles.whatsNewContainer}>
      <Typography variant="h4" sx={{margin: "2rem 0"}}>Why Choose Garuda2x ?</Typography>
      <Grid container spacing={2}>
        <WhyChooseGarudaItem
          title="Explore Onboarded Agents"
          textContent="Browse a curated library of agents designed to solve real-world challenges in healthcare, biology, and beyond. Each agent is meticulously onboarded, ensuring quality and interoperability."
        />
        <WhyChooseGarudaItem
          title="Install and Launch with Ease"
          textContent="Add agents to your personalized Garuda dashboard with a single click. Launch them directly from the platform and experience hassle-free integration with your existing workflows."
        />
        <WhyChooseGarudaItem
          title="Discover and Connect"
          textContent="Find related agents that complement your work. Garuda2x enables seamless discovery of tools/agents that expand possibilities and streamline operations."
        />
        <WhyChooseGarudaItem
          title="Data Navigation and Transfer"
          textContent="Transfer data effortlessly across agents to create a truly interoperable ecosystem. Garuda2x is built to break silos and make data flow seamlessly."
        />
        <WhyChooseGarudaItem
          title="Build Workflow Pipelines with Recipes"
          textContent="Create custom recipes by chaining agents into a workflow pipeline. These recipes enable automation and efficient execution of complex processes across multiple agents."
        />
      </Grid>
    </div>
  );
};

export const GarudaInAction = () => {
  return (
    <div className={styles.whatsNewContainer}>
      <Typography variant="h4" sx={{margin: "1rem 0"}}>See Garuda2x in Action</Typography>
      <Typography variant="body1">
        Join the growing list of professionals and companies transforming their
        workflows with Garuda2x. Whether you're in healthcare, research, or
        enterprise, Garuda2x is designed to meet your needs and empower your
        success.
      </Typography>
    </div>
  );
};

export const LearnAboutPlatform = () => (
  <Stack className={styles.rootContainer} spacing={4}>
    <WhatsNewInGaruda />
    <Divider sx={{ marginY: 2 }} />
    <WhyChooseGaruda />
    <Divider sx={{ marginY: 2 }} />
    <GarudaInAction />
    <Divider sx={{ marginY: 2 }} />
    <div>
      <RightImageAllignedContainer
        children={
          <AboutPortalContent
            headerText="Built By Garuda"
            portalFirstName="Garuda"
            portalSecondName="Desktop"
            infoText={aboutDesktopClientText}
            shouldContainDownloadButton={true}
          />
        }
        image={DesktopClientScreenshot}
        dataTestIdForImage="desktop-client-screenshot"
      />
      <LeftImageAllignedContainer
        children={
          <AboutPortalContent
            headerText="Made For You"
            portalFirstName="Agent"
            portalSecondName="Builder"
            infoText={aboutGadgetBuilderText}
          />
        }
        image={GadgetBuilderScreenshot}
        dataTestIdForImage="gadget-builder-screenshot"
      />
      <RightImageAllignedContainer
        children={
          <AboutPortalContent
            headerText="Made For You"
            portalFirstName="Group"
            portalSecondName="Admin"
            infoText={aboutGroupAdminPortal}
          />
        }
        image={GroupAdminScreenshot}
        dataTestIdForImage="group-admin-screenshot"
      />
      <Divider sx={{ marginY: 2 }} />
    </div>
  </Stack>
);
